import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FirefliesMetadata } from '../types';

import { useWidgetStore } from '@/common/stores/useWidgetStore';

interface FirefliesConfigProps {
  widgetId: string;
  onClose: () => void;
  metadata?: Record<string, unknown>;
}

const FIREFLIES_API_KEY_URL = 'https://app.fireflies.ai/integrations/custom/fireflies';

export default function FirefliesConfig({ widgetId, onClose, metadata }: FirefliesConfigProps) {
  const { t } = useTranslation();
  const { updateWidget } = useWidgetStore();
  const meta = metadata as FirefliesMetadata | undefined;
  const [apiKey, setApiKey] = useState(meta?.apiKey ?? '');
  const [userEmail, setUserEmail] = useState(meta?.userEmail ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateWidget(widgetId, {
        metadata: {
          ...metadata,
          apiKey: apiKey.trim(),
          userEmail: userEmail.trim(),
        },
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      await updateWidget(widgetId, {
        metadata: {
          ...metadata,
          apiKey: '',
          userEmail: '',
        },
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-gray-800">{t('fireflies.configTitle')}</h3>
      <div className="flex flex-col gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('fireflies.apiKeyLabel')}
          </label>
          <input
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder={t('fireflies.apiKeyPlaceholder')}
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <a
            className="text-xs text-blue-600 hover:underline mt-1 inline-block"
            href={FIREFLIES_API_KEY_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('fireflies.getApiKey')}
          </a>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('fireflies.userEmailLabel')}
          </label>
          <input
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder={t('fireflies.userEmailPlaceholder')}
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">{t('fireflies.userEmailHint')}</p>
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          className="px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          disabled={!apiKey.trim() || saving}
          onClick={handleSave}
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
        {meta?.apiKey && (
          <button
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={handleDisconnect}
          >
            {t('fireflies.disconnect')}
          </button>
        )}
        <button
          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={onClose}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
